import React, { createContext, useCallback, useEffect, useState } from "react";

import {
    createCustomer,
    deleteCustomer as deleteCustomerApi,
    fetchCustomer,
    fetchCustomersList,
    updateCustomer as updateCustomerApi,
} from "../api/customersApi";
import { DEFAULT_CUSTOMERS_PAGE_SIZE } from "../constants/config";

export const CustomersContext = createContext();

const initialPagination = {
    currentPage: 1,
    totalPages: 1,
    totalCustomers: 0,
    limit: DEFAULT_CUSTOMERS_PAGE_SIZE,
};

export const Provider = (props) => {
    // Lightweight full list used for forms, dropdowns, and table joins.
    const [customers, setCustomers] = useState();

    // Paginated slice for the Customers list page; populated on demand.
    const [customersPage, setCustomersPage] = useState();
    const [customersPagination, setCustomersPagination] = useState(initialPagination);
    const [customersPageLoading, setCustomersPageLoading] = useState(false);

    const getCustomersList = useCallback(async () => {
        try {
            const data = await fetchCustomersList();
            setCustomers(data);
        } catch (error) {
            console.log("Retrieving Customers List Error: ", error);
        }
    }, []);

    useEffect(() => {
        getCustomersList();
    }, [getCustomersList]);

    const getCustomersPage = useCallback(async (options = {}) => {
        const {
            page = 1,
            limit = DEFAULT_CUSTOMERS_PAGE_SIZE,
            search,
            contractor,
            balance,
            withStats = true,
            sortBy,
            sortOrder,
        } = options;

        setCustomersPageLoading(true);
        try {
            const data = await fetchCustomersList({
                page,
                limit,
                search,
                contractor,
                balance,
                withStats,
                sortBy,
                sortOrder,
            });

            // Newer API: { customers, pagination }. Older deployments may still
            // return a bare array; fall back to a client-side slice in that case
            // so the page still renders during a partial rollout.
            if (Array.isArray(data)) {
                const start = (page - 1) * limit;
                setCustomersPage(data.slice(start, start + limit));
                setCustomersPagination({
                    limit,
                    currentPage: page,
                    totalPages: Math.max(Math.ceil(data.length / limit), 1),
                    totalCustomers: data.length,
                });
            } else {
                setCustomersPage(data.customers || []);
                setCustomersPagination(data.pagination || initialPagination);
            }
            return data;
        } catch (error) {
            console.log("Retrieving Customers Page Error: ", error);
        } finally {
            setCustomersPageLoading(false);
        }
    }, []);

    const addCustomer = async (newCustomer) => {
        try {
            await createCustomer(newCustomer);
            getCustomersList();
        } catch (error) {
            console.log("Adding Customer Error: ", error);
            return error;
        }
    };

    const getCustomer = async (customerId) => {
        try {
            return await fetchCustomer(customerId);
        } catch (error) {
            console.log("Retrieving Customer Error: ", error);
        }
    };

    const updateCustomer = async (customerId, updatedCustomer) => {
        try {
            const data = await updateCustomerApi(customerId, updatedCustomer);
            setCustomers((prev) =>
                prev
                    ? prev.map((c) => (c._id === customerId ? { ...c, ...updatedCustomer } : c))
                    : prev,
            );
            return data;
        } catch (error) {
            console.log("Updating Customer Error: ", error);
        }
    };

    const deleteCustomer = async (customerId) => {
        try {
            await deleteCustomerApi(customerId);
            getCustomersList();
        } catch (error) {
            console.log("Deleting Customer Error: ", error);
            return error;
        }
    };

    return (
        <CustomersContext.Provider
            value={{
                customers,
                customersPage,
                customersPagination,
                customersPageLoading,
                action: {
                    addCustomer,
                    deleteCustomer,
                    getCustomer,
                    getCustomersList,
                    getCustomersPage,
                    setCustomers,
                    updateCustomer,
                },
            }}
        >
            {props.children}
        </CustomersContext.Provider>
    );
};
